import React from 'react';
import { Box, useTheme } from '@mui/material';
import { FiltersProvider, useFilters } from '../../contexts/FiltersContext';
import { AnalyticsDataProvider } from '../../contexts/AnalyticsDataContext';
import SidePanel from '../../components/analytics/SidePanel/SidePanelOptimized';
import TopPanel from '../../components/analytics/TopPanel/TopPanel';
import Dashboard from './Dashboard';
import { SplitViewProvider, useSplitView } from '../../contexts/SplitViewContext';

const DashboardPageContent: React.FC = () => {
  const { isSplitView } = useSplitView();
  const theme = useTheme();
  const { timeFilter, fromToDate } = useFilters();

  return (
    <Box sx={{
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      '@media print': {
        height: 'auto',
        overflow: 'visible',
      },
    }}>
      {/* Side Panel - Filter Sidebar - Hidden in split view */}
      {!isSplitView && <SidePanel />}

      {/* Main Content Area */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        '@media print': {
          overflow: 'visible',
        },
      }}>
        {/* Top Panel - Time Filters and Export - Visible only in single view at the top */}
        {!isSplitView && <TopPanel pageType="emissions" />}

        {/* Dashboard Content - KPI Cards, Tabs, Emission Tables */}
        <Box id="page-print-area" sx={{
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
          '@media print': {
            overflow: 'visible',
            display: 'block',
          },
        }}>
          {/* First View (Left) - Uses Root Providers */}
          <Box sx={{
            flex: 1,
            overflow: 'hidden',
            borderRight: isSplitView ? `1px solid ${theme.palette.divider}` : 'none',
            transition: 'all 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            '@media print': {
              overflow: 'visible',
              display: 'block',
            },
          }}>
            {isSplitView && <TopPanel pageType="emissions" showSplitViewControls={false} />}
            <Box sx={{
              flex: 1,
              overflow: 'auto',
              '@media print': {
                overflow: 'visible',
              },
            }}>
              <Dashboard />
            </Box>
          </Box>

          {/* Second View (Right) - Uses Scoped Providers */}
          {isSplitView && (
            <Box sx={{
              flex: 1,
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              animation: 'fadeIn 0.5s ease-in-out',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <FiltersProvider initialTimeFilter={timeFilter} initialFromToDate={fromToDate}>
                <AnalyticsDataProvider>
                  <TopPanel pageType="emissions" />
                  <Box sx={{ flex: 1, overflow: 'auto' }}>
                    <Dashboard />
                  </Box>
                </AnalyticsDataProvider>
              </FiltersProvider>
            </Box>
          )}
        </Box>
      </Box>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateX(20px); }
            to { opacity: 1; transform: translateX(0); }
          }
        `}
      </style>
    </Box>
  );
};

const DashboardPage: React.FC = () => {
  return (
    <SplitViewProvider>
      <FiltersProvider>
        <AnalyticsDataProvider>
          <DashboardPageContent />
        </AnalyticsDataProvider>
      </FiltersProvider>
    </SplitViewProvider>
  );
};

export default DashboardPage;

